/** BodyControlHandler / BodyControlPanel, RigidBodyRotator (+ SmoothMover) and CameraPreviewHandler. */
import { GameBehaviour } from '../game.ts';
import { BodyType, type SimBody, type SimCollider, type SimGo } from '../world.ts';
import { FrameCounter, moveTowardsV, type V2 } from '../unity.ts';
import { bool, list, num } from '../props.ts';
import type { ControlPanelLike, PlayerController } from '../player.ts';
import { ActivatorBase } from './activators.ts';
import { playerOf } from './util.ts';

const v2 = (d: any, def: V2 = { x: 0, y: 0 }): V2 => ({ x: num(d?.x, def.x), y: num(d?.y, def.y) });

// ---------- BodyControlPanel ----------

export class BodyControlPanel extends ActivatorBase implements ControlPanelLike {
  playerControlling: PlayerController | null = null;

  startControl(p: PlayerController) {
    this.playerControlling = p;
    this.activate();
  }

  endControl() {
    this.playerControlling = null;
    this.deactivate();
  }

  override onTriggerEnter(other: SimCollider) {
    const p = playerOf(other);
    if (p && !p.isUsingControlPanel) p.controlPanelInRange = this;
  }

  override onTriggerExit(other: SimCollider) {
    const p = playerOf(other);
    if (p && p.controlPanelInRange === this) {
      if (p.isUsingControlPanel) p.endControlPanel();
      p.controlPanelInRange = null;
    }
  }
}

// ---------- BodyControlHandler ----------

const enum ControlTypes {
  Nothing,
  LeftXAxis,
  LeftYAxis,
  RightXAxis,
  RightYAxis,
}

interface RigidbodyMetadata {
  xAxisControls: ControlTypes;
  xInputAxisScale: number;
  yAxisControls: ControlTypes;
  yInputAxisScale: number;
}

interface RigidbodyControlInfo {
  rigidbody: SimBody;
  meta: RigidbodyMetadata;
  startPosition: V2;
}

export class BodyControlHandler extends ActivatorBase {
  minPressedTime = new FrameCounter();
  maxPressedTime = new FrameCounter();
  cooldownTime = new FrameCounter();
  controlSpeed = 0.2;
  controlAcceleration = 0.001;
  leftControlStart: V2 = { x: 0, y: 0 };
  rightControlStart: V2 = { x: 0, y: 0 };
  xMin = -1;
  xMax = 1;
  yMin = -1;
  yMax = 1;
  leftControlPosition: V2 = { x: 0, y: 0 };
  rightControlPosition: V2 = { x: 0, y: 0 };
  leftControlVelocity: V2 = { x: 0, y: 0 };
  rightControlVelocity: V2 = { x: 0, y: 0 };
  desiredSwitchState = false;
  hasInputSinceLastStateChange = false;
  rigidBodies: RigidbodyControlInfo[] = [];
  controlPanels: BodyControlPanel[] = [];
  override noSave = ['rigidBodies', 'controlPanels'];

  override awake() {
    const d = this.data;
    this.minPressedTime = FrameCounter.from(d.minPressedTime);
    this.maxPressedTime = FrameCounter.from(d.maxPressedTime);
    this.cooldownTime = FrameCounter.from(d.cooldownTime);
    this.controlSpeed = num(d.controlSpeed, 0.2);
    this.controlAcceleration = num(d.controlAcceleration, 0.001);
    this.leftControlStart = v2(d.leftControlStart);
    this.rightControlStart = v2(d.rightControlStart);
    this.xMin = num(d.xMin, -1);
    this.xMax = num(d.xMax, 1);
    this.yMin = num(d.yMin, -1);
    this.yMax = num(d.yMax, 1);
  }

  override start() {
    this.leftControlPosition = { ...this.leftControlStart };
    this.rightControlPosition = { ...this.rightControlStart };
    this.rigidBodies = [];
    for (const rm of list(this.data.uiRigidBodiesOrGameObjects)) {
      const go = this.refGo(rm?.rigidbodyOrParent);
      if (!go) continue;
      const meta: RigidbodyMetadata = {
        xAxisControls: num(rm.xAxisControls) as ControlTypes,
        xInputAxisScale: num(rm.xInputAxisScale, 1),
        yAxisControls: num(rm.yAxisControls) as ControlTypes,
        yInputAxisScale: num(rm.yInputAxisScale, 1),
      };
      const bodies = go.body ? [go.body] : go.descendants().filter((g) => g.active && g.body).map((g) => g.body!);
      for (const rigidbody of bodies) this.rigidBodies.push({ rigidbody, meta, startPosition: rigidbody.position });
    }
    for (const ri of this.rigidBodies) {
      if (ri.rigidbody.type === BodyType.Static) continue;
      ri.rigidbody.managed = true;
      ri.rigidbody.hasKV = true;
      ri.rigidbody.resetKV();
    }
    this.controlPanels = this.go.getBehavioursInChildren(BodyControlPanel, false);
    for (const r of list(this.data.uiControlPanels)) {
      const cp = this.refBehaviour(r, BodyControlPanel);
      if (cp && !this.controlPanels.includes(cp)) this.controlPanels.push(cp);
    }
  }

  override fixedUpdate() {
    let dl: V2 = { x: 0, y: 0 }, dr: V2 = { x: 0, y: 0 };
    let n = 0;
    this.desiredSwitchState = false;
    for (const cp of this.controlPanels) {
      const p = cp.playerControlling;
      if (!p) continue;
      dl = { x: dl.x + p.input.movement.x * this.controlSpeed, y: dl.y + p.input.movement.y * this.controlSpeed };
      dr = { x: dr.x + p.input.control.x * this.controlSpeed, y: dr.y + p.input.control.y * this.controlSpeed };
      n++;
      if (p.jumpDown) this.desiredSwitchState = true;
      if (p.input.jumpPressed) this.hasInputSinceLastStateChange = true;
    }
    if (n > 0) {
      dl = { x: dl.x / n, y: dl.y / n };
      dr = { x: dr.x / n, y: dr.y / n };
    }
    this.handleSwitchState();
    this.leftControlVelocity = moveTowardsV(this.leftControlVelocity, dl, this.controlAcceleration);
    this.rightControlVelocity = moveTowardsV(this.rightControlVelocity, dr, this.controlAcceleration);
    this.integrate(this.leftControlPosition, this.leftControlVelocity);
    this.integrate(this.rightControlPosition, this.rightControlVelocity);
    this.updateRigidBodies();
  }

  private integrate(pos: V2, vel: V2) {
    if (vel.x === 0 && vel.y === 0) return;
    pos.x += vel.x;
    pos.y += vel.y;
    if (pos.x < this.xMin) {
      pos.x = this.xMin;
      vel.x = 0;
    }
    if (pos.x > this.xMax) {
      pos.x = this.xMax;
      vel.x = 0;
    }
    if (pos.y < this.yMin) {
      pos.y = this.yMin;
      vel.y = 0;
    }
    if (pos.y > this.yMax) {
      pos.y = this.yMax;
      vel.y = 0;
    }
  }

  private updateRigidBodies() {
    const l = { x: this.leftControlPosition.x - this.leftControlStart.x, y: this.leftControlPosition.y - this.leftControlStart.y };
    const r = { x: this.rightControlPosition.x - this.rightControlStart.x, y: this.rightControlPosition.y - this.rightControlStart.y };
    for (const ri of this.rigidBodies) {
      const rb = ri.rigidbody;
      if (rb.go.destroyed) continue;
      const m = ri.meta, s = ri.startPosition;
      const cur = rb.position;
      const np = { ...cur };
      const axis = (t: ControlTypes, start: number): number | null => {
        switch (t) {
          case ControlTypes.LeftXAxis: return l.x * m.xInputAxisScale + start;
          case ControlTypes.LeftYAxis: return l.y * m.yInputAxisScale + start;
          case ControlTypes.RightXAxis: return r.x * m.xInputAxisScale + start;
          case ControlTypes.RightYAxis: return r.y * m.yInputAxisScale + start;
          default: return null;
        }
      };
      np.x = axis(m.xAxisControls, s.x) ?? np.x;
      np.y = axis(m.yAxisControls, s.y) ?? np.y;
      if (np.x !== cur.x || np.y !== cur.y) rb.movePosition(np);
    }
  }

  private handleSwitchState() {
    if (this.minPressedTime.frames > 0) this.minPressedTime.count();
    this.maxPressedTime.count();
    this.cooldownTime.count();
    if (!this.isActivated) {
      if (this.desiredSwitchState && !this.cooldownTime.isRunningNotFinished() && this.hasInputSinceLastStateChange) {
        this.activate();
        if (this.minPressedTime.frames > 0) this.minPressedTime.restart();
        if (this.maxPressedTime.frames > 0) this.maxPressedTime.restart();
      }
    } else if (this.minPressedTime.frames === 0 || this.minPressedTime.isFinished()) {
      const maxTimeIsUp = this.maxPressedTime.frames > 0 && this.maxPressedTime.isFinished();
      if (!this.desiredSwitchState || maxTimeIsUp) {
        this.deactivate();
        if (maxTimeIsUp) this.hasInputSinceLastStateChange = false;
        this.cooldownTime.restart();
      }
    }
  }

  override afterRestore() {
    this.updateRigidBodies();
  }
}

// ---------- SmoothMover ----------

const enum AcceleratorState {
  Accelerating,
  Stopping,
  Stopped,
}

function wrap(x: number, period: number) {
  if (period <= 0) return x;
  const r = x % period;
  return r < 0 ? r + period : r;
}

function shortestDelta(to: number, from: number, period: number) {
  if (period <= 0) return to - from;
  const half = period * 0.5;
  let d = (to - from) % period;
  if (d <= -half) d += period;
  else if (d > half) d -= period;
  return d;
}

export class SmoothMover {
  currentPos = 0;
  currentSpeed = 0;
  endPos = 0;
  acceleration = 0;
  maxSpeed = 0;
  useAngles = false;
  anglePeriod = 360;
  useEndPos = true;
  endlessMoveDirection = 1;
  state = AcceleratorState.Stopped;
  stoppedThisFrame = false;

  static from(d: any): SmoothMover {
    const m = new SmoothMover();
    if (!d) return m;
    m.currentPos = num(d.currentPos);
    m.currentSpeed = num(d.currentSpeed);
    m.endPos = num(d.endPos);
    m.acceleration = num(d.acceleration);
    m.maxSpeed = num(d.maxSpeed);
    m.useAngles = bool(d.useAngles);
    m.anglePeriod = num(d.anglePeriod, 360);
    m.useEndPos = bool(d.useEndPos, true);
    m.endlessMoveDirection = num(d.endlessMoveDirection, 1);
    m.state = num(d.state, AcceleratorState.Stopped) as AcceleratorState;
    return m;
  }

  cloneState(): SmoothMover {
    return Object.assign(new SmoothMover(), this);
  }

  private get stoppingDistance() {
    return (this.currentSpeed * this.currentSpeed) / (2 * this.acceleration);
  }

  runFixedUpdate() {
    const eps = 1e-4;
    this.stoppedThisFrame = false;
    const signedDist = this.useAngles ? shortestDelta(this.endPos, this.currentPos, this.anglePeriod) : this.endPos - this.currentPos;
    const dir = this.useEndPos ? Math.sign(signedDist) : this.endlessMoveDirection;
    const dist = Math.abs(signedDist);
    if (this.useEndPos && this.state !== AcceleratorState.Stopped && dist <= eps && Math.abs(this.currentSpeed) <= eps) {
      this.currentPos = this.endPos;
      this.currentSpeed = 0;
      this.state = AcceleratorState.Stopped;
      this.stoppedThisFrame = true;
      return;
    }
    const movingTowardTarget = Math.sign(this.currentSpeed) === dir || this.currentSpeed === 0;
    if (this.state === AcceleratorState.Accelerating) {
      if (movingTowardTarget && dist <= this.stoppingDistance && this.useEndPos) {
        this.state = AcceleratorState.Stopping;
      } else {
        this.currentSpeed += (this.useEndPos ? dir : this.endlessMoveDirection) * this.acceleration;
        if (Math.abs(this.currentSpeed) > this.maxSpeed) this.currentSpeed = Math.sign(this.currentSpeed) * this.maxSpeed;
        this.currentPos += this.currentSpeed;
        if (this.useAngles) this.currentPos = wrap(this.currentPos, this.anglePeriod);
      }
    }
    if (this.state === AcceleratorState.Stopping) {
      const lastSpeed = this.currentSpeed;
      if (this.currentSpeed > 0) this.currentSpeed -= this.acceleration;
      else if (this.currentSpeed < 0) this.currentSpeed += this.acceleration;
      if (Math.sign(this.currentSpeed) !== Math.sign(lastSpeed)) {
        this.currentSpeed = 0;
        if (this.useEndPos && dist <= this.acceleration + Math.abs(lastSpeed)) this.currentPos = this.endPos;
        this.state = AcceleratorState.Stopped;
        this.stoppedThisFrame = true;
      }
      this.currentPos += this.currentSpeed;
      if (this.useAngles) this.currentPos = wrap(this.currentPos, this.anglePeriod);
      if (this.useEndPos) {
        const nd = this.useAngles ? shortestDelta(this.endPos, this.currentPos, this.anglePeriod) : this.endPos - this.currentPos;
        if (Math.sign(nd) !== dir || (this.useAngles && Math.abs(nd) < 0.01)) {
          this.currentPos = this.endPos;
          this.currentSpeed = 0;
          this.state = AcceleratorState.Stopped;
          this.stoppedThisFrame = true;
        }
      }
    }
  }

  start() {
    this.state = AcceleratorState.Accelerating;
  }

  stop(stopImmediately = false) {
    this.state = stopImmediately ? AcceleratorState.Stopped : AcceleratorState.Stopping;
  }
}

// ---------- RigidBodyRotator ----------

const enum PauseType {
  PauseImmediately,
  PauseWhenFinished,
}

const enum RotatorState {
  Waiting,
  Running,
}

interface RotatePoint {
  endAngle: number;
  rotateSpeed: number;
  acceleration: number;
  pauseTime: FrameCounter;
}

interface RotateBodyState {
  rb: SimBody;
  initialOffsetFromPivot: V2;
  localAngleOffset: number;
}

export class RigidBodyRotator extends GameBehaviour {
  activator: ActivatorBase | null = null;
  rotatePoints: RotatePoint[] = [];
  activatorPauseType = PauseType.PauseImmediately;
  masterRotateSpeed = 0;
  masterAcceleration = 0;
  rotateDirection = 1;
  startRotateIndex = 0;
  pivot: SimGo | null = null;
  currentRotateIndex = 0;
  angleMover = new SmoothMover();
  state = RotatorState.Running;
  lastActivatorStatus = false;
  heardActivator = false;
  baseStates: RotateBodyState[] = [];
  baseAngle = 0;
  override noSave = ['activator', 'pivot', 'baseStates'];

  private get currentRotatePoint(): RotatePoint | null {
    return this.rotatePoints[this.currentRotateIndex] ?? null;
  }

  override awake() {
    const d = this.data;
    this.rotatePoints = list(d.rotatePoints).map((p: any) => ({
      endAngle: num(p?.endAngle),
      rotateSpeed: num(p?.rotateSpeed),
      acceleration: num(p?.acceleration),
      pauseTime: FrameCounter.from(p?.pauseTime),
    }));
    this.activatorPauseType = num(d.activatorPauseType) as PauseType;
    this.masterRotateSpeed = num(d.masterRotateSpeed);
    this.masterAcceleration = num(d.masterAcceleration);
    this.rotateDirection = num(d.rotateDirection, 1);
    this.startRotateIndex = num(d.startRotateIndex);
    this.currentRotateIndex = num(d.currentRotateIndex);
    this.angleMover = SmoothMover.from(d.angleMover);
  }

  override start() {
    this.activator = this.refBehaviour(this.data.activator, ActivatorBase as any) as ActivatorBase | null;
    this.pivot = this.refGo(this.data.pivot) ?? this.go;
    const m = this.angleMover;
    m.useAngles = true;
    const bodies = this.go.descendants().filter((g) => g.active && g.body).map((g) => g.body!);
    this.baseAngle = m.currentPos;
    const pp = this.pivot.position;
    this.baseStates = bodies.map((rb) => {
      // MovePosition / MoveRotation do nothing on static bodies; they keep following their parent transform
      if (rb.type !== BodyType.Static) rb.managed = true;
      const p = rb.position;
      return { rb, initialOffsetFromPivot: { x: p.x - pp.x, y: p.y - pp.y }, localAngleOffset: rb.rotation - this.baseAngle };
    });
    const n = this.rotatePoints.length;
    if (n > 0) {
      this.currentRotateIndex = this.startRotateIndex % n;
      m.useEndPos = true;
      const startAngle = this.rotatePoints[(this.currentRotateIndex === 0 ? n : this.currentRotateIndex) - 1].endAngle;
      this.rotateAllBodies(startAngle);
      m.currentPos = startAngle;
    } else {
      m.useEndPos = false;
      m.endlessMoveDirection = this.rotateDirection;
      this.activatorPauseType = PauseType.PauseImmediately;
    }
    this.updateAngleMover();
    if (this.activator && !this.activator.isActivated) m.stop();
    else m.start();
  }

  override fixedUpdate() {
    const m = this.angleMover;
    const a = this.activator;
    if (a) {
      const changed = this.lastActivatorStatus !== a.isActivated;
      this.lastActivatorStatus = a.isActivated;
      this.heardActivator = true;
      if (changed) {
        if (a.isActivated) {
          m.start();
          if (this.heardActivator) this.sound('rotator');
        }
        if (!a.isActivated && this.activatorPauseType === PauseType.PauseImmediately) m.stop();
      }
    }
    if (this.state === RotatorState.Running) {
      m.runFixedUpdate();
      if (m.stoppedThisFrame && this.rotatePoints.length > 0) {
        const cp = this.currentRotatePoint;
        if (cp && cp.pauseTime.frames > 0) {
          cp.pauseTime.restart();
          this.state = RotatorState.Waiting;
        } else if (!a || a.isActivated) this.startNextRotatePoint();
      }
      this.rotateAllBodies(m.currentPos);
    } else {
      const cp = this.currentRotatePoint!;
      cp.pauseTime.count();
      if (cp.pauseTime.isFinished() && (!a || a.isActivated)) this.startNextRotatePoint();
    }
  }

  private rotateAllBodies(angle: number) {
    const rad = ((angle - this.baseAngle) * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    const pp = this.pivot!.position;
    for (const c of this.baseStates) {
      const o = c.initialOffsetFromPivot;
      c.rb.movePosition({ x: pp.x + o.x * cos - o.y * sin, y: pp.y + o.x * sin + o.y * cos });
      // the mover wraps to [0, 360): rotate the short way instead of spinning back across the wrap
      const cur = c.rb.rotation;
      c.rb.moveRotation(cur + shortestDelta(c.localAngleOffset + angle, cur, 360));
    }
  }

  private startNextRotatePoint() {
    this.currentRotateIndex = (this.currentRotateIndex + 1) % this.rotatePoints.length;
    this.updateAngleMover();
    this.angleMover.start();
    this.state = RotatorState.Running;
  }

  private updateAngleMover() {
    const cp = this.currentRotatePoint;
    if (!cp) return;
    const m = this.angleMover;
    m.acceleration = cp.acceleration || this.masterAcceleration;
    m.maxSpeed = cp.rotateSpeed || this.masterRotateSpeed;
    m.endPos = cp.endAngle;
    cp.pauseTime.reset();
  }
}

// ---------- CameraPreviewHandler ----------

const enum CameraPreviewMoveSpeed {
  Slow,
  Regular,
  Fast,
}

export class CameraPreviewHandler extends GameBehaviour {
  lookAtPosition: V2 = { x: 0, y: 0 };
  pauseAtDestinationCounter = new FrameCounter();
  moveSpeed = CameraPreviewMoveSpeed.Slow;
  hasBeenActivated = false;
  activator: ActivatorBase | null = null;
  override noSave = ['activator'];

  override awake() {
    const d = this.data;
    this.lookAtPosition = v2(d.lookAtPosition);
    this.pauseAtDestinationCounter = FrameCounter.from(d.pauseAtDestinationCounter);
    this.moveSpeed = num(d.moveSpeed) as CameraPreviewMoveSpeed;
  }

  override start() {
    this.activator = this.refBehaviour(this.data.activator, ActivatorBase as any) as ActivatorBase | null;
    this.activator?.listeners.push((on) => {
      if (on && !this.hasBeenActivated && this.game.camera) {
        this.game.camera.setLookAtHandler(this);
        this.hasBeenActivated = true;
      }
    });
  }

  getCameraPreviewMoveSpeed(): number {
    return this.moveSpeed === CameraPreviewMoveSpeed.Slow ? 10 : this.moveSpeed === CameraPreviewMoveSpeed.Regular ? 30 : 50;
  }
}
